import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import useFailures from '@/data/hook/question/useFailures';
import useSubmit from '@/data/hook/question/useSubmit';
import { Question } from '@/models/Question';

export type FormEntry = {
  submit: () => Promise<Question>;
  validate: () => Promise<boolean>;
  hasChanges: boolean;
};

export type SaveSuccess = { identifier: string; result: Question };

export type SubmitAllResult = {
  successes: SaveSuccess[];
  failedIdentifiers: string[];
};

type QuestionFormsOrchestratorProps = {
  registerForm: (identifier: string, entry: FormEntry) => void;
  unregisterForm: (identifier: string) => void;
  updateFormEntry: (identifier: string, partial: Partial<FormEntry>) => void;
  submitAll: () => Promise<SubmitAllResult>;
  submitForm: (identifier: string) => Promise<void>;
  resetFailed: (...identifier: string[]) => void;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  failedIdentifiers: Set<string>;
};

const QuestionFormsOrchestratorContext =
  createContext<QuestionFormsOrchestratorProps>(
    {} as QuestionFormsOrchestratorProps,
  );

type OrchestratorProviderProps = PropsWithChildren<{
  onSaved?: (result: SubmitAllResult) => void;
}>;

export default function QuestionFormsOrchestratorProvider({
  children,
  onSaved,
}: OrchestratorProviderProps) {
  const registryRef = useRef<Map<string, FormEntry>>(new Map());
  const [registryVersion, setRegistryVersion] = useState(0);

  const {
    failedIdentifiers,
    markFailed,
    markSucceeded,
    reset: resetFailed,
  } = useFailures();
  const { submitForm, submitAll, isSaving } = useSubmit({
    registry: registryRef,
    onSuccess: markSucceeded,
    onFail: markFailed,
    onSaved,
  });

  const hasUnsavedChanges =
    registryVersion > 0 &&
    Array.from(registryRef.current.values()).some(e => e.hasChanges);

  const registerForm = useCallback((identifier: string, entry: FormEntry) => {
    registryRef.current.set(identifier, entry);
    setRegistryVersion(v => v + 1);
  }, []);

  const unregisterForm = useCallback((identifier: string) => {
    registryRef.current.delete(identifier);
    setRegistryVersion(v => v + 1);
  }, []);

  const updateFormEntry = useCallback(
    (identifier: string, partial: Partial<FormEntry>) => {
      const existing = registryRef.current.get(identifier);
      if (!existing) return;
      const prev = existing.hasChanges;
      const updated = { ...existing, ...partial };
      registryRef.current.set(identifier, updated);
      if (partial.hasChanges !== undefined && partial.hasChanges !== prev) {
        setRegistryVersion(v => v + 1);
      }
    },
    [],
  );

  return (
    <QuestionFormsOrchestratorContext.Provider
      value={{
        registerForm,
        unregisterForm,
        updateFormEntry,
        submitAll,
        resetFailed,
        submitForm,
        isSaving,
        hasUnsavedChanges,
        failedIdentifiers,
      }}
    >
      {children}
    </QuestionFormsOrchestratorContext.Provider>
  );
}

export const useQuestionFormsOrchestrator = () =>
  useContext(QuestionFormsOrchestratorContext);
